import { useEffect, useState } from "react";
import CreateGameModal from "./CreateGameModal";
import { FaAndroid, FaApple, FaTrash, FaEdit } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../utils/api";

const Dashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [games, setGames] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [gameToEdit, setGameToEdit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);


  useEffect(() => {
    const fetchGames = async () => {
      try {
        const res = await api.get("/api/games");
        setGames(res.data);
      } catch (err) {
        console.error("Error fetching games:", err);
      }
    };
    fetchGames();
  }, []);

  const handleCreateGame = (newGame) => {
    setGames((prev) => [...prev, newGame]);
    setIsModalOpen(false);
    toast.success("Game created successfully!");
  };

  const handleUpdateGame = (updatedGame) => {
    setGames(prev => prev.map(game => 
      game.gameId === updatedGame.gameId ? { ...game, ...updatedGame } : game
    ));
    setGameToEdit(null);
    setIsModalOpen(false);
    toast.success("Game updated successfully!");
  };

  const openEditModal = (game) => {
    setGameToEdit(game);
    setIsModalOpen(true);
  };

  const toggleStatus = async (index, status) => {
    const game = games[index];
    const field = `${status}Ads`;
    const updatedValue = !game[field];

    try {
      await api.put(
        `/api/game/update/${game.gameId}`,
        { [field]: updatedValue }
      );
      setGames((prev) => prev.map((g, i) => (i === index ? { ...g, [field]: updatedValue } : g)));
      toast.success(`Ad status turned ${updatedValue ? "ON" : "OFF"} for ${status}`);
    } catch (err) {
      console.error("Failed to toggle status:", err);
      toast.error("Failed to toggle ad status.");
    }
  };

  const handleVersionUpdate = async (index, status, version, versionCode) => {
    const game = games[index];
    try {
      await api.put(
        `/api/game/update/${game.gameId}`,
        { [`${status}Version`]: version, [`${status}VersionCode`]: versionCode }
      );
      setGames((prev) =>
        prev.map((g, i) =>
          i === index ? { ...g, [`${status}Version`]: version, [`${status}VersionCode`]: versionCode } : g
        )
      );
      toast.success(`${status} version info updated`);
    } catch (err) {
      console.error("Failed to update version:", err);
      toast.error("Failed to update version info.");
    }
  };

  const openDeleteModal = (game) => {
    setGameToDelete(game);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/game/delete/${gameToDelete.gameId}`);
      setGames((prev) => prev.filter((g) => g.gameId !== gameToDelete.gameId));
      toast.success("Game deleted successfully.");
    } catch (error) {
      console.error("Error deleting game:", error);
      toast.error("Failed to delete game.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setGameToDelete(null);
    }
  };
  

  return (
    <div className="w-full mb-4 px-4">
      <button
        className="bg-blue-600 hover:bg-indigo-800 text-white px-4 py-2 rounded-md mb-2"
        onClick={() => {
          setGameToEdit(null);
          setIsModalOpen(true);
        }}
      >
        Create Game
      </button>
      <h1 className="text-xl font-semibold">Games</h1>

      <div className="bg-white p-4 rounded-lg shadow-md w-full h-full">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b text-center text-gray-600">
              <th className="p-2 text-left">Title</th>
              <th className="p-2">Live</th>
              <th className="p-2">In Review</th>
              <th className="p-2">Testing</th>
              <th className="p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {games.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-gray-400 p-4">
                  No games added yet.
                </td>
              </tr>
            ) : (
              games.map((game, index) => (
                <tr key={index} className={`border-b ${index % 2 === 0 ? "bg-gray-100" : "bg-gray-300"}`}>
                  <td className="p-2 flex items-center space-x-2">
                    {game.gameIcon && (
                      <img src={game.gameIcon} alt="Game Icon" className="w-6 h-6 rounded-full" />
                    )}
                    <span>{game.gameTitle}</span>
                    {game.platform === "android" ? (
                      <FaAndroid className="text-green-600 ml-2" size={20} />
                    ) : (
                      <FaApple className="text-blue-600 ml-2" size={20} />
                    )}
                    <span className="text-gray-500 text-sm mr-2">[{game.gameId}]</span>
                  </td>

                  {["live", "inReview", "testing"].map((status) => (
                    <td key={status} className="p-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={game[`${status}Ads`]}
                            onChange={() => toggleStatus(index, status)}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-checked:bg-blue-600 rounded-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                        </label>
                        <input
                          type="text"
                          value={game[`${status}Version`] || ""}
                          onChange={(e) =>
                            setGames((prev) =>
                              prev.map((g, i) =>
                                i === index ? { ...g, [`${status}Version`]: e.target.value } : g
                              )
                            )
                          }
                          placeholder="Ver"
                          className="border px-2 py-1 text-sm w-14 rounded-md"
                        />
                        <input
                          type="text"
                          value={game[`${status}VersionCode`] || ""}
                          onChange={(e) =>
                            setGames((prev) =>
                              prev.map((g, i) =>
                                i === index ? { ...g, [`${status}VersionCode`]: e.target.value } : g
                              )
                            )
                          }
                          placeholder="Code"
                          className="border px-2 py-1 text-sm w-16 rounded-md"
                        />
                        <button
                          className="bg-blue-600 text-white px-2 py-1 text-sm rounded-md transition duration-200 hover:bg-blue-800"
                          onClick={() =>
                            handleVersionUpdate(
                              index,
                              status,
                              game[`${status}Version`],
                              game[`${status}VersionCode`]
                            )
                          }
                        >
                          Update
                        </button>
                      </div>
                    </td>
                  ))}
                  <td className="p-2 text-center">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(game)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit Game"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => openDeleteModal(game)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Game"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    <CreateGameModal
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        setGameToEdit(null);
      }}
      onCreate={handleCreateGame}
      onUpdate={handleUpdateGame}
      gameToEdit={gameToEdit}
    />

    {showDeleteModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
          <h2 className="text-lg font-semibold text-gray-800 text-center mb-4">
            Are you sure you want to delete <br />
            <span className="text-red-600">{gameToDelete?.gameTitle}</span>?
          </h2>
          <div className="flex justify-center gap-4 mt-4">
            <button
              className="bg-gray-300 hover:bg-gray-600 text-gray-800 font-semibold py-2 px-4 rounded transition duration-200"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              className="bg-red-600 hover:bg-red-800 text-white font-semibold py-2 px-4 rounded transition duration-200 flex items-center justify-center gap-2"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 text-white animate-spin fill-white"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              ) : (
                "Confirm Delete"
              )}
            </button>
          </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default Dashboard;

